import { MediaAssetUrls } from '@core/media/models/media-urls.models';
import { isManagedMediaDeliveryUrl } from '@core/media/utils/media-image.util';
import {
  ProductModifierGroupDraft,
  ProductModifierOptionDraft,
  ProductFormChangeSnapshot,
  ProductVariantEditor,
} from '@features/catalog/models/product-form-draft.models';
import {
  CreateProductBundleModifierGroupInput,
} from '@features/catalog/models/product-input.models';
import { ProductModifierGroup, ProductModifierOption } from '@features/catalog/models/product.models';

export function mapModifierOptionToDraft(
  option: ProductModifierOption,
  createTempId: () => string
): ProductModifierOptionDraft {
  return {
    tempId: createTempId(),
    id: option.id,
    definitionId: option.definitionId ?? null,
    name: option.name,
    description: option.description ?? '',
    priceDelta: Number(option.priceDelta ?? 0),
    variantPrices: (option.variantPrices ?? []).map((variantPrice) => ({
      variantId: variantPrice.variantId,
      variantName: variantPrice.variantName ?? null,
      variantPosition: variantPrice.variantPosition ?? null,
      priceDelta: Number(variantPrice.priceDelta ?? 0),
    })),
    imageUrl: option.imageUrl ?? null,
    imageUrls: option.imageUrls,
    maxQuantity: option.maxQuantity ?? null,
    position: option.position ?? null,
    active: option.active ?? true,
  };
}

export function mapModifierGroupToDraft(
  group: ProductModifierGroup,
  createTempId: () => string
): ProductModifierGroupDraft {
  return {
    tempId: createTempId(),
    id: group.id,
    definitionId: group.definitionId ?? null,
    name: group.name,
    description: group.description ?? '',
    selectionType: group.selectionType,
    required: group.required,
    minSelections: group.minSelections,
    maxSelections: group.maxSelections ?? null,
    position: group.position ?? null,
    active: group.active,
    options: group.options.map((option) => mapModifierOptionToDraft(option, createTempId)),
  };
}

export function mapModifierGroupsFromProduct(
  groups: readonly ProductModifierGroup[],
  createTempId: () => string
): ProductModifierGroupDraft[] {
  return groups.map((group) => mapModifierGroupToDraft(group, createTempId));
}

export function patchModifierOptionImage(
  groups: readonly ProductModifierGroupDraft[],
  groupTempId: string,
  optionTempId: string,
  patch: { imageUrl: string | null; imageUrls?: MediaAssetUrls | null }
): ProductModifierGroupDraft[] {
  return groups.map((group) =>
    group.tempId === groupTempId
      ? {
          ...group,
          options: group.options.map((option) =>
            option.tempId === optionTempId
              ? {
                  ...option,
                  imageUrl: patch.imageUrl,
                  imageUrls:
                    'imageUrls' in patch ? (patch.imageUrls ?? undefined) : option.imageUrls,
                }
              : option
          ),
        }
      : group
  );
}

/** @deprecated Usar patchModifierOptionImage */
export function patchModifierOptionImageUrl(
  groups: readonly ProductModifierGroupDraft[],
  groupTempId: string,
  optionTempId: string,
  imageUrl: string | null
): ProductModifierGroupDraft[] {
  return patchModifierOptionImage(groups, groupTempId, optionTempId, { imageUrl });
}

function mapModifierOptionToSnapshotOption(
  option: ProductModifierOptionDraft
): ProductFormChangeSnapshot['modifierGroups'][number]['options'][number] {
  return {
    id: option.id ?? null,
    name: option.name,
    description: option.description,
    priceDelta: Number(option.priceDelta),
    variantPrices: (option.variantPrices ?? []).map((variantPrice) => ({
      variantId: variantPrice.variantId ?? null,
      variantName: variantPrice.variantName ?? null,
      variantPosition: variantPrice.variantPosition ?? null,
      priceDelta: Number(variantPrice.priceDelta),
    })),
    imageUrl: option.imageUrl,
    maxQuantity: option.maxQuantity,
    position: option.position,
    active: option.active,
  };
}

function mapModifierGroupToSnapshotGroup(
  group: ProductModifierGroupDraft
): ProductFormChangeSnapshot['modifierGroups'][number] {
  return {
    id: group.id ?? null,
    name: group.name,
    description: group.description,
    selectionType: group.selectionType,
    required: group.required,
    minSelections: group.minSelections,
    maxSelections: group.maxSelections,
    position: group.position,
    active: group.active,
    options: group.options.map(mapModifierOptionToSnapshotOption),
  };
}

export function mapModifierGroupsToChangeSnapshot(
  groups: readonly ProductModifierGroupDraft[]
): ProductFormChangeSnapshot['modifierGroups'] {
  return groups.map(mapModifierGroupToSnapshotGroup);
}

export function mapModifierGroupsToStructureSnapshot(
  groups: ProductFormChangeSnapshot['modifierGroups']
): unknown {
  return groups.map((group) => ({
    id: group.id,
    name: group.name,
    description: group.description,
    selectionType: group.selectionType,
    required: group.required,
    minSelections: group.minSelections,
    maxSelections: group.maxSelections,
    position: group.position,
    active: group.active,
    options: group.options.map((option) => ({
      id: option.id,
      name: option.name,
      description: option.description,
      priceDelta: option.priceDelta,
      variantPrices: option.variantPrices,
      imageUrl: option.imageUrl,
      maxQuantity: option.maxQuantity,
      position: option.position,
      active: option.active,
    })),
  }));
}

export function mapModifierGroupsToBundleInput(
  modifierGroups: readonly ProductModifierGroupDraft[],
  productVariants: readonly ProductVariantEditor[]
): CreateProductBundleModifierGroupInput[] {
  const mapped = modifierGroups
    .map((group, index) => {
      const groupDefinitionId = group.definitionId?.trim() || undefined;
      return {
      id: group.id?.trim() ? group.id : undefined,
      productModifierGroupDefinitionId: groupDefinitionId,
      name: group.name.trim(),
      description: group.description.trim() || null,
      selectionType: group.selectionType,
      required: group.required,
      minSelections: Math.max(0, Number(group.minSelections ?? 0)),
      maxSelections: group.maxSelections == null ? null : Math.max(0, Number(group.maxSelections)),
      position: group.position ?? index,
      active: group.active,
      options: group.options
        .map((option, optionIndex) => {
          const definitionId = option.definitionId?.trim() || undefined;
          return {
            id: option.id?.trim() ? option.id : undefined,
            productModifierOptionDefinitionId: definitionId,
            name: option.name.trim(),
            description: option.description.trim() || null,
            priceDelta: option.variantPrices.length ? 0 : Math.max(0, Number(option.priceDelta ?? 0)),
            variantPrices: option.variantPrices
              .map((variantPrice) => {
                const variant = productVariants.find(
                  (item) =>
                    item.id === variantPrice.variantId ||
                    item.position === variantPrice.variantPosition ||
                    item.name.trim() === (variantPrice.variantName ?? '').trim()
                );
                return {
                  variantId: variant?.isNew ? null : (variant?.id ?? variantPrice.variantId ?? null),
                  variantName: variant?.name.trim() || variantPrice.variantName || null,
                  variantPosition: variant?.position ?? variantPrice.variantPosition ?? null,
                  priceDelta: Math.max(0, Number(variantPrice.priceDelta ?? 0)),
                };
              })
              .filter(
                (variantPrice) =>
                  variantPrice.variantId != null ||
                  variantPrice.variantName != null ||
                  variantPrice.variantPosition != null
              ),
            imageUrl: isManagedMediaDeliveryUrl(option.imageUrl) ? option.imageUrl!.trim() : null,
            maxQuantity: option.maxQuantity == null ? null : Math.max(1, Number(option.maxQuantity)),
            position: option.position ?? optionIndex,
            active: option.active,
          };
        })
        .filter((option) => option.name.length > 0),
    };
    })
    .filter((group) => group.name.length > 0);

  return mapped;
}
